import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ShopHeader from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useEditions } from "@/context/EditionsContext";
import {
  PRODUCTS, SIZES, COLORS, FRAMING, priceFor, eur, coverOf,
} from "@/lib/shop";

// Quick Shop — compact overlay on the tile (Part 6). Thin-ruled, on cream.
function QuickShop({ p, onClose }) {
  const { addItem } = useCart();
  const { t, lang } = useLanguage();
  const [color, setColor] = useState("noir");
  const [size, setSize] = useState("50x70");
  const [framing, setFraming] = useState("unframed");
  const price = priceFor(size, framing);

  const add = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      product_id: p.id,
      product_name: p.name,
      color,
      size,
      framing,
      frame_color: framing === "framed" ? "oak" : null,
      quantity: 1,
      unit_price: price,
      image: p.prints[color],
    });
    toast.success(t("product.added"));
    onClose();
  };

  return (
    <div
      data-testid={`quickshop-${p.id}`}
      onClick={(e) => e.preventDefault()}
      className="absolute inset-0 z-10 flex flex-col justify-end bg-[#faf8f5]/95 backdrop-blur-[2px] p-5"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#a89f8e]">{t("quick.shop")}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {COLORS.map((c) => (
          <button
            key={c.id}
            data-testid={`qs-color-${p.id}-${c.id}`}
            onClick={(e) => { e.preventDefault(); setColor(c.id); }}
            className={`border px-3 py-1.5 text-[11px] font-light transition-colors duration-200 ${
              color === c.id ? "border-[#111111] bg-[#111111] text-[#faf8f5]" : "border-[#111111]/25 text-[#111111]/80"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {SIZES.map((s) => (
          <button
            key={s.id}
            data-testid={`qs-size-${p.id}-${s.id}`}
            onClick={(e) => { e.preventDefault(); setSize(s.id); }}
            className={`border px-3 py-1.5 text-[11px] font-light transition-colors duration-200 ${
              size === s.id ? "border-[#111111] bg-[#f1ede7] text-[#111111]" : "border-[#111111]/25 text-[#111111]/80"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="mt-2 flex gap-1.5">
        {FRAMING.map((f) => (
          <button
            key={f.id}
            data-testid={`qs-framing-${p.id}-${f.id}`}
            onClick={(e) => { e.preventDefault(); setFraming(f.id); }}
            className={`flex-1 border px-3 py-1.5 text-[11px] font-light transition-colors duration-200 ${
              framing === f.id ? "border-[#111111] bg-[#f1ede7] text-[#111111]" : "border-[#111111]/25 text-[#111111]/80"
            }`}
          >
            {t(`product.${f.id}`)}
          </button>
        ))}
      </div>
      <button
        onClick={add}
        data-testid={`qs-add-${p.id}`}
        className="mt-3 w-full bg-[#111111] px-4 py-3 text-xs font-light tracking-wide text-[#faf8f5] transition-opacity duration-300 hover:opacity-85"
      >
        {t("product.add")} — {eur(price, lang)}
      </button>
    </div>
  );
}

export default function Shop() {
  const { t, lang } = useLanguage();
  const { liveRemaining, liveEditionTotal, liveAlmostGone } = useEditions();
  const [quickFor, setQuickFor] = useState(null);
  const from = Math.min(...SIZES.map((s) => s.prices.unframed));

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <ShopHeader solid />
      <main className="pt-24 md:pt-32 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
            {t("shop.kicker")}
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#111111]">
            {t("shop.title.a")}
            <span className="italic">{t("shop.title.x")}</span>
            {t("shop.title.b")}
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-[#111111]/70">
            {t("shop.intro")}
          </p>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l gallery-line">
            {PRODUCTS.map((p) => {
              const cardColor = p.cardColor || "bicolor";
              const ed = liveEditionTotal("50x70", cardColor);
              const rem = liveRemaining(p.id, "50x70", cardColor);
              const allSoldOut = SIZES.every((s) =>
                COLORS.every((c) => liveRemaining(p.id, s.id, c.id) === 0)
              );
              const scarce = SIZES.some((s) =>
                COLORS.some((c) => liveAlmostGone(p.id, s.id, c.id))
              );
              return (
                <div
                  key={p.id}
                  data-testid={`shop-card-${p.id}`}
                  className={`group relative border-r border-b gallery-line ${allSoldOut ? "opacity-60 grayscale" : ""}`}
                >
                  <Link to={`/shop/${p.id}`} className="block">
                    <div className="relative overflow-hidden bg-white">
                      <img
                        src={coverOf(p)}
                        alt={p.tagline}
                        loading="lazy"
                        className="aspect-[4/5] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
                      />
                      {!allSoldOut && quickFor !== p.id && (
                        <button
                          data-testid={`quickshop-open-${p.id}`}
                          onClick={(e) => { e.preventDefault(); setQuickFor(p.id); }}
                          className="absolute bottom-4 left-1/2 -translate-x-1/2 border border-[#111111]/40 bg-[#faf8f5]/95 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#111111] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-[#111111] hover:text-[#faf8f5]"
                        >
                          {t("quick.shop")}
                        </button>
                      )}
                      {!allSoldOut && quickFor === p.id && (
                        <QuickShop p={p} onClose={() => setQuickFor(null)} />
                      )}
                    </div>
                    <div className="flex items-end justify-between gap-4 px-6 py-6">
                      <div>
                        <h3 className="font-display italic text-xl md:text-2xl tracking-tight text-[#111111]">
                          {p.name}
                        </h3>
                        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#a89f8e]">
                          {allSoldOut ? (
                            <span className="text-[#111111]">{t("grid.soldOut")}</span>
                          ) : (
                            <>
                              <span data-testid={`grid-counter-${p.id}`}>
                                {rem} / {ed} {t("grid.remaining")}
                              </span>
                              {scarce && (
                                <span
                                  data-testid={`almost-gone-${p.id}`}
                                  className="ml-3 border border-[#111111]/30 px-2 py-0.5 text-[10px] tracking-[0.18em] text-[#111111]"
                                >
                                  {t("shop.almostGone")}
                                </span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      <span className="text-sm font-light text-[#111111]/70">
                        {t("shop.from")} {eur(from, lang)}
                      </span>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-sm font-light text-[#111111]/50" data-testid="shop-upcoming">
            {t("shop.upcoming")}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
